<script setup lang="ts">
import {
  PhClockCounterClockwise,
  PhCoins,
  PhFiles,
  PhFloppyDisk,
  PhInfo,
  PhPrinter,
} from '@phosphor-icons/vue';
import { models } from 'cesg-shared';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AtaChangelog from '../ata-changelog.vue';
import AtaCostsTable from '../ata-costs-table.vue';
import AtaStatusPicker from '../AtaStatusPicker.vue';
import {
  useAtaDetailsLogic,
  type UseAtaDetailsLogicProps,
} from './logic/useAtaDetailsLogic';
import { AtaDetailsCard } from './sub-components/ata-details-card';
import { AtaDetailsSection } from './sub-components/ata-details-section';
import { DateField } from './sub-components/date-field';
import { PdfMerger } from './sub-components/pdf-merger';
import { RadioGroup } from './sub-components/radio-group';
import { YesNoRadioGroup } from './sub-components/yes-no-radio-group';
import type {
  AtaDocumentPayload,
  AtaFieldKey,
  AtaFormData,
  AtaSaveCallbacks,
} from './types';

const props = defineProps<UseAtaDetailsLogicProps>();
const emit = defineEmits<{
  save: [
    formData: AtaFormData,
    document: AtaDocumentPayload,
    callbacks: AtaSaveCallbacks,
  ];
}>();
const { state, action, formData, readOnly } = useAtaDetailsLogic(props, emit);
const { t } = useI18n();

const isDisabled = (key: AtaFieldKey) =>
  state.value.permissions.fields[key] !== 'edit';

const priorityOptions = [1, 2, 3, 4];

const ATA_DOCUMENT_MAX_BYTES = 30 * 1024 * 1024;

const descriptionRef = ref<HTMLTextAreaElement | null>(null);
const answerRef = ref<HTMLTextAreaElement | null>(null);

// Grow to fit the content, never below the height the rows attribute gives.
const syncTextareaHeight = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = '';
  const borders = el.offsetHeight - el.clientHeight;
  el.style.height = `${el.scrollHeight + borders}px`;
};

const syncTextareaHeights = () => {
  syncTextareaHeight(descriptionRef.value);
  syncTextareaHeight(answerRef.value);
};

watch(
  () => [state.value.formData.description, state.value.formData.answer],
  () => nextTick(syncTextareaHeights),
  { immediate: true },
);

const generalMainRef = ref<HTMLElement | null>(null);
const generalDocHeight = ref<number | null>(null);

let generalMainResizeObserver: ResizeObserver | null = null;

const shouldSyncGeneralDocHeight = () =>
  typeof window !== 'undefined' && window.innerWidth > 1200;

const syncGeneralDocHeight = () => {
  if (!shouldSyncGeneralDocHeight()) {
    generalDocHeight.value = null;
    return;
  }
  generalDocHeight.value = generalMainRef.value?.offsetHeight ?? null;
};

onMounted(() => {
  syncTextareaHeights();
  syncGeneralDocHeight();

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', syncGeneralDocHeight);
  }

  if (generalMainRef.value && typeof ResizeObserver !== 'undefined') {
    generalMainResizeObserver = new ResizeObserver(() => {
      syncGeneralDocHeight();
    });
    generalMainResizeObserver.observe(generalMainRef.value);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', syncGeneralDocHeight);
  }

  if (generalMainResizeObserver) {
    generalMainResizeObserver.disconnect();
    generalMainResizeObserver = null;
  }
});

defineExpose({ formData, readOnly });
</script>

<template>
  <form @submit.prevent="action.save()">
    <div class="flex-grid-page">
      <div class="header mb-4">
        <div v-if="state.isCreate">
          <h1 class="is-size-4">{{ t('ata.details.newATATitle') }}</h1>
        </div>
        <div v-else>
          <h1 class="is-size-4 ata-title-heading">
            <span class="ata-title-prefix">{{
              t('ata.nameSerialNr', {
                serialNumber: state.formData.serialNumber,
              })
            }}</span>
            -
            <span class="ata-title-text">{{ state.formData.title }}</span>
          </h1>
        </div>
        <hr class="ata-title-divider" />
      </div>
      <div class="sections-layout">
        <AtaDetailsSection
          :icon="PhInfo"
          :title="t('ata.details.sections.generalInformation')"
        >
          <div class="general-layout">
            <div ref="generalMainRef" class="general-main">
              <AtaDetailsCard
                :icon="PhInfo"
                :title="t('ata.details.cards.ataInformation')"
              >
                <div class="meta-data">
                  <div class="columns is-multiline">
                    <div class="column is-half">
                      <div class="field is-required">
                        <label class="label">{{
                          t('ata.details.form.title')
                        }}</label>
                        <div class="control">
                          <input
                            required
                            class="input"
                            type="text"
                            id="title"
                            :value="state.formData.title"
                            @input="
                              action.updateField(
                                'title',
                                ($event.target as HTMLInputElement).value,
                              )
                            "
                            :disabled="isDisabled('title')"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="column is-half">
                      <div class="field is-required">
                        <label class="label">{{
                          t('ata.details.form.status')
                        }}</label>
                        <div class="control">
                          <AtaStatusPicker
                            :model-value="state.formData.status"
                            @update:model-value="action.setStatus"
                            :available-statuses="
                              state.permissions.availableStatuses
                            "
                            :disabled="state.permissions.status !== 'edit'"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="column is-full">
                      <div class="field">
                        <label class="label">{{
                          t('ata.details.form.description')
                        }}</label>
                        <div class="control">
                          <textarea
                            ref="descriptionRef"
                            class="textarea auto-grow-textarea"
                            id="description"
                            rows="3"
                            :value="state.formData.description"
                            @input="
                              action.updateField(
                                'description',
                                ($event.target as HTMLTextAreaElement).value,
                              )
                            "
                            :disabled="isDisabled('description')"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                    <div class="column is-full">
                      <div class="field">
                        <label class="label">{{
                          t('ata.details.form.answer')
                        }}</label>
                        <div class="control">
                          <textarea
                            ref="answerRef"
                            class="textarea auto-grow-textarea"
                            id="answer"
                            rows="2"
                            :value="state.formData.answer"
                            @input="
                              action.updateField(
                                'answer',
                                ($event.target as HTMLTextAreaElement).value,
                              )
                            "
                            :disabled="isDisabled('answer')"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="columns is-multiline">
                    <div class="column is-half">
                      <div class="field">
                        <label class="label">{{
                          t('ata.details.form.shouldBeRegulated')
                        }}</label>
                        <div class="control">
                          <YesNoRadioGroup
                            name="shouldBeRegulated"
                            :model-value="state.formData.shouldBeRegulated"
                            @update:model-value="
                              action.updateField('shouldBeRegulated', $event)
                            "
                            :disabled="isDisabled('shouldBeRegulated')"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="column is-half">
                      <div class="field">
                        <label class="label">{{
                          t('ata.details.form.leadsToTimeExtension')
                        }}</label>
                        <div class="control">
                          <YesNoRadioGroup
                            name="leadsToTimeExtension"
                            :model-value="state.formData.leadsToTimeExtension"
                            @update:model-value="
                              action.updateField(
                                'leadsToTimeExtension',
                                $event,
                              )
                            "
                            :disabled="isDisabled('leadsToTimeExtension')"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="column is-half">
                      <div class="field">
                        <label class="label">{{
                          t('ata.details.form.pricingModel')
                        }}</label>
                        <div class="control">
                          <RadioGroup
                            name="pricingModel"
                            :model-value="state.formData.pricingModel"
                            @update:model-value="
                              action.updateField('pricingModel', $event)
                            "
                            required
                            :disabled="isDisabled('pricingModel')"
                            :options="[
                              {
                                id: 'pricingModel-fast',
                                value: models.domain.AtaPricingModel.Fast,
                                label: t(models.domain.AtaPricingModel.Fast),
                              },
                              {
                                id: 'pricingModel-lopande',
                                value: models.domain.AtaPricingModel.Lopande,
                                label: t(
                                  models.domain.AtaPricingModel.Lopande,
                                ),
                              },
                            ]"
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      class="column is-half"
                      v-if="
                        state.formData.pricingModel ===
                        models.domain.AtaPricingModel.Fast
                      "
                    >
                      <div class="field">
                        <label class="label">{{
                          t('ata.details.form.shouldBeForecasted')
                        }}</label>
                        <div class="control">
                          <YesNoRadioGroup
                            name="shouldBeForecasted"
                            :model-value="state.formData.shouldBeForecasted"
                            @update:model-value="
                              action.updateField('shouldBeForecasted', $event)
                            "
                            required
                            :disabled="isDisabled('shouldBeForecasted')"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="column is-half" v-else></div>

                    <div class="column is-half">
                      <div class="field">
                        <label class="label">{{
                          t('ata.details.form.type')
                        }}</label>
                        <div class="control">
                          <RadioGroup
                            name="type"
                            :model-value="state.formData.type"
                            @update:model-value="
                              action.updateField('type', $event)
                            "
                            :disabled="isDisabled('type')"
                            :options="[
                              {
                                id: 'type-B',
                                value: models.domain.AtaType.B,
                                label: t(models.domain.AtaType.B),
                              },
                              {
                                id: 'type-HG',
                                value: models.domain.AtaType.HG,
                                label: t(models.domain.AtaType.HG),
                              },
                              {
                                id: 'type-other',
                                value: models.domain.AtaType.Other,
                                label: t(models.domain.AtaType.Other),
                              },
                            ]"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="column is-half">
                      <div class="field">
                        <label class="label">{{
                          t('ata.details.form.priority')
                        }}</label>
                        <div class="control">
                          <div class="select">
                            <select
                              :value="state.formData.priority"
                              @change="
                                action.updateField(
                                  'priority',
                                  Number(
                                    ($event.target as HTMLSelectElement).value,
                                  ),
                                )
                              "
                              id="priority"
                              :disabled="isDisabled('priority')"
                            >
                              <option
                                v-for="priority in priorityOptions"
                                :key="priority"
                                :value="priority"
                              >
                                {{ priority }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="columns is-multiline mb-1">
                    <div class="column is-half-tablet date-column">
                      <DateField
                        id="sentDate"
                        :label="t('ata.details.form.sentDate')"
                        :model-value="state.formData.sentDate"
                        @update:model-value="
                          action.updateField('sentDate', $event)
                        "
                        :disabled="isDisabled('sentDate')"
                      />
                    </div>
                    <div class="column is-half-tablet date-column">
                      <DateField
                        id="deadlineDate"
                        :label="t('ata.details.form.deadlineDate')"
                        :model-value="state.formData.deadlineDate"
                        @update:model-value="
                          action.updateField('deadlineDate', $event)
                        "
                        :disabled="isDisabled('deadlineDate')"
                      />
                    </div>
                    <div class="column is-half-tablet date-column">
                      <DateField
                        id="answerReceivedDate"
                        :label="t('ata.details.form.answerReceivedDate')"
                        :model-value="state.formData.answerReceivedDate"
                        @update:model-value="
                          action.updateField('answerReceivedDate', $event)
                        "
                        :disabled="isDisabled('answerReceivedDate')"
                      />
                    </div>

                    <div class="column is-half-tablet date-column">
                      <DateField
                        id="revisionDate"
                        :label="t('ata.details.form.revisionDate')"
                        :model-value="state.formData.revisionDate"
                        @update:model-value="
                          action.updateField('revisionDate', $event)
                        "
                        :disabled="isDisabled('revisionDate')"
                      />
                    </div>
                  </div>
                </div>
              </AtaDetailsCard>
            </div>

            <div
              v-if="
                state.permissions.fields.documents === 'edit' ||
                props.initialDocument ||
                state.hasStagedDocument ||
                state.isDocumentLoading
              "
              class="general-doc"
              :style="
                generalDocHeight
                  ? {
                      height: `${generalDocHeight}px`,
                    }
                  : undefined
              "
            >
              <PdfMerger
                :icon="PhFiles"
                :title="t('ata.details.cards.ataDocuments')"
                :file-name="state.documentFileName"
                :download-file-name="state.downloadFileName"
                :initial-document="props.initialDocument"
                :is-loading="state.isDocumentLoading"
                :read-only="state.permissions.fields.documents !== 'edit'"
                :max-bytes="ATA_DOCUMENT_MAX_BYTES"
                @document-change="action.setDocument"
              />
            </div>
          </div>
        </AtaDetailsSection>

        <AtaDetailsSection
          :icon="PhCoins"
          :title="t('ata.details.sections.costs')"
        >
          <AtaDetailsCard
            :icon="PhCoins"
            :title="t('ata.details.cards.ataCosts')"
            :content-class="'no-padding'"
          >
            <ata-costs-table
              :data="state.formData.costs as models.AtaCost[]"
              :read-only="state.permissions.fields.costs !== 'edit'"
              :debit-norm-rates="state.debitNormRates"
            ></ata-costs-table>
          </AtaDetailsCard>
        </AtaDetailsSection>

        <AtaDetailsSection
          :icon="PhClockCounterClockwise"
          :title="t('ata.changelogSectionTitle')"
        >
          <AtaDetailsCard
            :icon="PhClockCounterClockwise"
            :title="t('ata.changelogTitle')"
            :content-class="['changelog-content', 'no-padding']"
          >
            <AtaChangelog :changes="state.changelogs" />
          </AtaDetailsCard>
        </AtaDetailsSection>
      </div>

      <div class="save">
        <button
          v-if="state.permissions.save === 'visible'"
          class="button is-primary is-float-right"
          type="submit"
          :class="{ 'is-loading': state.isSaving }"
          :disabled="state.isSaving || !state.isDirty"
        >
          <PhFloppyDisk :size="18" class="mr-2" />
          {{ t('save') }}
        </button>
        <button
          class="button is-float-right mr-2"
          @click="action.print()"
          type="button"
          :disabled="state.isCreate"
          :title="
            state.isCreate ? t('ata.details.printAtaCreateDisabled') : undefined
          "
        >
          <PhPrinter :size="18" class="mr-2" />
          {{ t('ata.details.printAta') }}
        </button>
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
@use 'bulma/sass/utilities/initial-variables' as *;

.flex-grid-page {
  --bulma-column-gap: 0.5rem;
  --doc-handler-max-width: 36rem;
  --doc-handler-min-width: 34rem;

  display: flex;
  flex-direction: column;
  row-gap: 1rem;

  .sections-layout {
    display: flex;
    flex-direction: column;
    gap: 4rem;
  }

  .ata-title-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
    margin: 0;
  }

  .ata-title-prefix {
    color: $grey-darker;
  }

  .ata-title-text {
    color: inherit;
    font: inherit;
    line-height: inherit;
  }

  .ata-title-divider {
    width: 100%;
    margin: 0.5rem 0 0;
    border: none;
    border-top: 1px solid $grey-lighter;
  }

  .general-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) fit-content(
        var(--doc-handler-max-width)
      );
    gap: 1.5rem;
    align-items: start;
  }

  .general-main {
    min-width: 0;
  }

  .general-doc {
    min-width: var(--doc-handler-min-width);
    width: fit-content;
    max-width: var(--doc-handler-max-width);
    justify-self: start;
    display: flex;

    :deep(.ata-details-card) {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-height: 0;
    }
  }

  @media (max-width: 1200px) {
    .general-layout {
      grid-template-columns: minmax(0, 1fr);
    }

    .general-doc {
      min-width: 0;
      width: 100%;
    }
  }

  .meta-data {
    min-width: 0;
  }

  .auto-grow-textarea {
    resize: none;
    overflow-y: hidden;
  }

  @media screen and (min-width: 1536px) {
    .meta-data .column.date-column {
      width: 25%;
    }
  }

  :deep(.no-padding) {
    padding: 0;
    padding-top: 0.75rem;
    overflow: hidden;
  }

  :deep(.changelog-content) {
    max-height: 600px;
    overflow-y: auto;
    min-height: 0;
  }

  .save {
    position: sticky;
    bottom: 0;
    z-index: 10;
    padding: 1rem 0;
    background-color: $white;
    border-top: 1px solid $grey-lighter;

    &::after {
      content: '';
      display: table;
      clear: both;
    }
  }
}
</style>
